import { normalizeWorkspacesForUi } from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";

/**
 * Map POS `type` / `deviceType` (SK/EN variants) to canonical `turnstile` | `gate` for zone rules.
 * @param {unknown} raw
 * @returns {"turnstile" | "gate" | null}
 */
export function canonicalZoneAccessDeviceType(raw) {
  const folded = String(raw ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
  if (!folded) return null;
  if (folded === "turnstile" || folded === "turniket") return "turnstile";
  if (folded === "gate" || folded === "brana") return "gate";
  return null;
}

/**
 * Flat list of selectable devices for zones from POS workspace document (`data[]` workspaces, each with `devices[]`).
 * Only access devices: turnstile / gate (EN) and common SK labels (turniket, brána).
 *
 * @param {object|null|undefined} posWorkspaceDoc — raw document from GET /api/v1/access-system/posWorkspace
 * @returns {{
 *   workspaceId: string,
 *   workspaceName: string,
 *   deviceId: string,
 *   deviceType: string,
 *   deviceName: string,
 *   selectionKey: string,
 *   device: object,
 * }[]}
 */
export function listTurnstileGateDevicesFromPosWorkspaceDoc(posWorkspaceDoc) {
  const { data } = normalizeWorkspacesForUi(posWorkspaceDoc || {});
  const rows = [];
  for (const ws of data || []) {
    if (!ws || typeof ws !== "object") continue;
    const workspaceId =
      ws.uuid != null && String(ws.uuid).trim() !== ""
        ? String(ws.uuid).trim()
        : "";
    if (!workspaceId) continue;
    const workspaceName =
      typeof ws.name === "string" && ws.name.trim() !== ""
        ? ws.name.trim()
        : workspaceId;
    const devices = Array.isArray(ws.devices) ? ws.devices : [];
    for (const d of devices) {
      if (!d || typeof d !== "object") continue;
      const rawType = d.type ?? d.deviceType ?? "";
      const canonical = canonicalZoneAccessDeviceType(rawType);
      if (!canonical) continue;
      const deviceId =
        (d.deviceId != null && String(d.deviceId).trim() !== ""
          ? String(d.deviceId).trim()
          : null) ||
        (d.uuid != null && String(d.uuid).trim() !== ""
          ? String(d.uuid).trim()
          : null) ||
        (d.id != null && String(d.id).trim() !== ""
          ? String(d.id).trim()
          : "");
      if (!deviceId) continue;
      const deviceName =
        typeof d.name === "string" && d.name.trim() !== ""
          ? d.name.trim()
          : deviceId;
      const deviceType =
        typeof d.type === "string"
          ? d.type
          : typeof d.deviceType === "string"
            ? d.deviceType
            : canonical;
      rows.push({
        workspaceId,
        workspaceName,
        deviceId,
        deviceType,
        deviceName,
        selectionKey: `${workspaceId}::${deviceId}`,
        device: d,
      });
    }
  }
  return rows;
}

/**
 * @param {string} workspaceId
 * @param {string} deviceId
 */
export function makeZoneDeviceSelectionKey(workspaceId, deviceId) {
  return `${String(workspaceId).trim()}::${String(deviceId).trim()}`;
}

/**
 * Keeps validation working when a zone already references a device that no longer appears
 * in the current POS workspace document (e.g. removed workspace) — those rows are still
 * considered "known" for the form session.
 *
 * @param {ReturnType<typeof listTurnstileGateDevicesFromPosWorkspaceDoc>} selectableRows
 * @param {{ deviceId: string, workspaceId: string, deviceType: string, isActive?: boolean }[]} assignedDevices
 */
export function mergeEligibleDevicesWithAssigned(selectableRows, assignedDevices) {
  const map = new Map();
  (selectableRows || []).forEach((row) => {
    if (row && row.selectionKey) map.set(row.selectionKey, row);
  });
  (assignedDevices || []).forEach((d) => {
    if (!d || typeof d !== "object") return;
    const workspaceId = String(d.workspaceId ?? "").trim();
    const deviceId = String(d.deviceId ?? "").trim();
    if (!workspaceId || !deviceId) return;
    const k = makeZoneDeviceSelectionKey(workspaceId, deviceId);
    if (map.has(k)) return;
    map.set(k, {
      workspaceId,
      workspaceName: workspaceId,
      deviceId,
      deviceType: String(d.deviceType ?? "").trim() || "—",
      deviceName: deviceId,
      selectionKey: k,
    });
  });
  return Array.from(map.values());
}
